import Header from './Header';
import background from './img/softwarePartners.webp';
import MobileNavigation from './MobileNavigation';
import { CopyBlock, dracula } from 'react-code-blocks';
import webhooksExample from './img/WebhooksExample1.png';
import Footer from './Footer';
import { Link } from 'react-router-dom';
import { Section, openSection } from './DocSection';

const longText = { textWrapMode: 'wrap', textWrapStyle: 'auto', width: '100%' };
const linkStyle = { color: '#007bff', textDecoration: 'underline' };
const codeStyle = { overflowX: 'auto' };

const sections = [
  { id: 'create-a-webhook', title: 'Create a Webhook' },
  { id: 'retrieve-the-webhook-public-key', title: 'Retrieve the Webhook Public Key' },
  { id: 'webhook-payload-structure', title: 'Webhook Payload Structure' },
  { id: 'webhook-signing-and-security', title: 'Webhook Signing & Security' },
  { id: 'acknowledge-and-retry', title: 'Acknowledge & Retry' },
  { id: 'webhook-payload-examples', title: 'Webhook Payload Examples' },
  { id: 'recommended-end-to-end-flow', title: 'Recommended End-to-End Flow' }
];

const DeveloperWebhooks = () => {
  return (
    <>
      <MobileNavigation />
      <section className="bg-[#212121] woo-commerce">
        <div
          className="w-full pb-28"
          style={{
            backgroundImage: `url(${background})`,
            backgroundSize: '200% 200%',
            backgroundPosition: 'bottom center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <Header />
          <div className="p-4 row md:p-8 how">
            <h3 className="text-white large">iQ Pro+ Webhook API Guide</h3>
          </div>
          <div className="flex p-16 row flex-col justify-center flex-start gap-6 self-stretch rounded-[28px] bg-white !mb-10">
            <h2 className="text-black large">Overview</h2>
            <p className="text-black light" style={longText}>
              This document outlines the end-to-end flow for receiving and processing iQ Pro+
              webhooks. It covers webhook creation, signature verification and security,
              acknowledgement and retries, and the payloads you can expect to receive.
            </p>
            <p className="text-black light" style={longText}>
              Webhooks provide real-time updates and remove the need to poll the API for changes.
              Webhooks are triggered as <b>transactions</b>, <b>settlements</b>, and <b>invoices</b>{' '}
              are created or updated within the gateway.
            </p>
            <h2 className="font-normal text-black">On this page</h2>
            <ol className="ml-5 list-decimal gap-x-12 marker:text-gray-500 md:columns-2 [&>li]:break-inside-avoid [&>li]:py-1">
              {sections.map(({ id, title }) => (
                <li key={id} className="text-black light">
                  <a onClick={openSection} href={`#${id}`} style={linkStyle}>
                    {title}
                  </a>
                </li>
              ))}
            </ol>
            <h2 className="font-normal text-black">Prerequisites</h2>
            <p className="text-black light">Before creating a webhook, ensure the following:</p>
            <ul className="ml-5 list-disc">
              <li className="text-black light">
                You have <b>Gateway</b> access to iQ Pro+
              </li>
              <li className="text-black light">
                You have a publicly reachable <b>HTTPS</b> endpoint that accepts <code>POST</code>{' '}
                requests with a JSON body
              </li>
              <li className="text-black light">
                You have API credentials (Client ID and Client Secret) to retrieve the webhook
                public key. For more information, refer to the{' '}
                <Link to="/developer-tokenization" style={linkStyle}>
                  Tokenization guide
                </Link>
                , section <b>1. App Registration &amp; Authentication</b>.
              </li>
            </ul>

            <Section id="create-a-webhook" title="Create a Webhook" open>
              <p className="text-black light">In the iQ Pro+ UI, navigate to:</p>
              <p className="font-semibold text-black">Developer Hub → Webhooks → Create New +</p>
              <ol className="ml-5 list-decimal">
                <li className="text-black light">
                  Select the webhook type: <b>Transaction</b>, <b>Settlement</b>, or <b>Invoice</b>
                </li>
                <li className="text-black light">
                  Enter the URL of your receiving endpoint
                  <img src={webhooksExample} alt="Screenshot of Create New Webhook" />
                </li>
              </ol>
              <div className="my-4 callout">
                <p className="mb-0 text-black light">
                  <strong>NOTE:</strong> You can create up to a maximum of{' '}
                  <b>5 webhooks for each type</b> (Transaction, Settlement, and Invoice).
                </p>
              </div>
            </Section>

            <Section id="retrieve-the-webhook-public-key" title="Retrieve the Webhook Public Key">
              <p className="text-black light" style={longText}>
                Every webhook is signed by iQ Pro+ with a private key. To verify a webhook, you need
                the matching <b>public key</b> for your gateway.
              </p>
              <h2 className="font-normal text-black">Request</h2>
              <p className="font-semibold text-black">Endpoint</p>
              <CopyBlock
                text={`GET /api/gateway/{gatewayId}/configuration`}
                language="bash"
                theme={dracula}
                customStyle={codeStyle}
              />
              <p className="font-semibold text-black">Headers</p>
              <CopyBlock
                text={`Authorization: Bearer {TOKEN}
Accept: application/json`}
                language="text"
                theme={dracula}
                customStyle={codeStyle}
              />
              <h2 className="font-normal text-black">Response</h2>
              <p className="text-black light">
                The public key is returned in <code>data.webhookPublicKey</code>.
              </p>
              <table className="tg" style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '35%' }} />
                  <col style={{ width: '65%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="font-bold text-center text-black">Field</th>
                    <th className="font-bold text-center text-black">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <code>webhookPublicKey</code>
                    </td>
                    <td>
                      <code>data.webhookPublicKey</code>: RSA public key used to verify webhook
                      signatures
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="my-4 callout">
                <p className="mb-0 text-black light">
                  <strong>NOTE:</strong> <code>webhookPublicKey</code> may return <code>null</code>{' '}
                  until a webhook has been configured for the gateway. Complete{' '}
                  <a onClick={openSection} href="#create-a-webhook" style={linkStyle}>
                    Create a Webhook
                  </a>{' '}
                  first, then retrieve the key.
                </p>
              </div>
              <h2 className="font-normal text-black">Recommended Usage</h2>
              <ul className="ml-5 list-disc">
                <li className="text-black light">
                  Retrieve the public key once and store it securely with your integration
                  configuration
                </li>
                <li className="text-black light">
                  Store a separate key for <b>sandbox</b> and <b>production</b>; keys are not shared
                  between environments
                </li>
                <li className="text-black light">
                  Re-retrieve the key if signature verification begins failing for genuine webhooks
                </li>
              </ul>
            </Section>

            <Section id="webhook-payload-structure" title="Webhook Payload Structure">
              <p className="text-black light" style={longText}>
                Every webhook is delivered as a JSON <code>POST</code> body with the same top-level
                structure. The event data is contained in <code>resource</code>, and the signature
                is carried <b>in the body</b>, not in an HTTP header.
              </p>
              <h2 className="font-normal text-black">Core Webhook Model</h2>
              <CopyBlock
                text={`public class SignedMessage
{
    public string ResourceType { get; }
    public string SubType { get; }
    public object Resource { get; }
    public string TraceId { get; }
    public DateTime ActionDateTime { get; }
    public string HashAlg { get; }
    public string SignatureAlg { get; }
    public string Signature { get; }
}`}
                language="csharp"
                theme={dracula}
                customStyle={codeStyle}
              />
              <h2 className="font-normal text-black">Field Descriptions</h2>
              <table className="tg" style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '30%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '50%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="font-bold text-center text-black">Field</th>
                    <th className="font-bold text-center text-black">Type</th>
                    <th className="font-bold text-center text-black">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <code>resourceType</code>
                    </td>
                    <td>string</td>
                    <td>
                      Type of record that triggered the webhook (e.g., <code>TRANSACTION</code>,{' '}
                      <code>INVOICE</code>)
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <code>subType</code>
                    </td>
                    <td>string</td>
                    <td>
                      Action or subtype of the record (e.g., <code>Sale</code>)
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <code>resource</code>
                    </td>
                    <td>object</td>
                    <td>
                      The full record that was created or updated. <b>This is the signed data.</b>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <code>traceId</code>
                    </td>
                    <td>string</td>
                    <td>
                      Unique identifier for the webhook delivery, useful for logging and support
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <code>actionDateTime</code>
                    </td>
                    <td>datetime</td>
                    <td>Date and time the action occurred</td>
                  </tr>
                  <tr>
                    <td>
                      <code>hashAlg</code>
                    </td>
                    <td>string</td>
                    <td>
                      Hash algorithm used to sign the payload: <code>SHA256</code>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <code>signatureAlg</code>
                    </td>
                    <td>string</td>
                    <td>
                      Signature algorithm: <code>RSA</code>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <code>signature</code>
                    </td>
                    <td>string</td>
                    <td>
                      Base64-encoded RSA signature of the <code>resource</code> object
                    </td>
                  </tr>
                </tbody>
              </table>
            </Section>

            <Section id="webhook-signing-and-security" title="Webhook Signing & Security">
              <p className="text-black light" style={longText}>
                All webhooks must be verified <b>before</b> your system acts on them. Verification
                confirms the webhook was sent by iQ Pro+ and that the <code>resource</code> data was
                not altered in transit.
              </p>
              <h2 className="font-normal text-black">How Webhooks Are Signed</h2>
              <p className="text-black light">iQ Pro+ signs each webhook as follows:</p>
              <ol className="ml-5 list-decimal">
                <li className="text-black light">
                  The <code>resource</code> object is serialized to JSON
                </li>
                <li className="text-black light">The JSON is encoded as UTF-8 bytes</li>
                <li className="text-black light">
                  The bytes are hashed with <b>SHA-256</b>
                </li>
                <li className="text-black light">
                  The hash is signed with the gateway&apos;s RSA private key using{' '}
                  <b>PKCS#1 v1.5</b> padding
                </li>
                <li className="text-black light">
                  The signature is Base64 encoded and placed in the top-level <code>signature</code>{' '}
                  field of the body
                </li>
              </ol>
              <div className="my-4 callout">
                <p className="mb-0 text-black light">
                  <strong>IMPORTANT:</strong> Only the <code>resource</code> object is signed. The
                  top-level fields (<code>resourceType</code>, <code>subType</code>,{' '}
                  <code>traceId</code>, <code>actionDateTime</code>, <code>hashAlg</code>,{' '}
                  <code>signatureAlg</code>, and <code>signature</code>) are <b>not</b> part of the
                  signed data.
                </p>
              </div>
              <div className="my-4 callout">
                <p className="mb-0 text-black light">
                  <strong>NOTE:</strong> Webhooks are <b>not</b> signed with HMAC, and the signature
                  is <b>not</b> sent in a <code>Signature</code> HTTP header. Integrations built
                  against earlier documentation describing an HMAC SHA-256 header and signature UUID
                  should be updated to the RSA verification flow below.
                </p>
              </div>
              <h2 className="font-normal text-black">Verifying the Signature</h2>
              <p className="text-black light">To verify a webhook:</p>
              <ol className="ml-5 list-decimal">
                <li className="text-black light">Parse the request body as JSON</li>
                <li className="text-black light">
                  Confirm <code>resource</code> and <code>signature</code> are present. If either is
                  missing, reject the webhook.
                </li>
                <li className="text-black light">
                  Serialize the <code>resource</code> object back to JSON and encode it as UTF-8
                  bytes
                </li>
                <li className="text-black light">
                  Base64 decode the <code>signature</code> value (standard Base64, not URL-safe)
                </li>
                <li className="text-black light">
                  Verify the signature against the bytes using your <b>webhook public key</b>,{' '}
                  <b>SHA-256</b>, and <b>PKCS#1 v1.5</b> padding
                </li>
                <li className="text-black light">
                  If verification fails, reject the webhook and do not process it
                </li>
              </ol>
              <h2 className="font-normal text-black">Example (C#)</h2>
              <CopyBlock
                text={`using Newtonsoft.Json;
using System;
using System.Security.Cryptography;
using System.Text;

public static class SignatureValidator
{
    public static bool VerifySignature(SignedMessage message, string publicKey)
    {
        if (message.Resource == null)
        {
            throw new ArgumentNullException(nameof(message.Resource), "Resource field cannot be null");
        }

        string resourceJson = JsonConvert.SerializeObject(message.Resource);
        byte[] dataBytes = Encoding.UTF8.GetBytes(resourceJson);
        byte[] signatureBytes = Convert.FromBase64String(message.Signature);

        using (SHA256 sha256 = SHA256.Create())
        {
            byte[] hashBytes = sha256.ComputeHash(dataBytes);
            using (RSA rsa = RSA.Create())
            {
                // Note: Public key must be in PEM format
                rsa.ImportFromPem(publicKey.ToCharArray());
                return rsa.VerifyHash(hashBytes, signatureBytes, HashAlgorithmName.SHA256, RSASignaturePadding.Pkcs1);
            }
        }
    }
}`}
                language="csharp"
                theme={dracula}
                customStyle={codeStyle}
              />
              <h2 className="font-normal text-black">Serialization Requirements</h2>
              <p className="text-black light" style={longText}>
                Because the signature covers the serialized <code>resource</code> object, your
                serialized JSON must match the bytes iQ Pro+ signed. When re-serializing{' '}
                <code>resource</code>:
              </p>
              <ul className="ml-5 list-disc">
                <li className="text-black light">
                  Preserve the <b>original key order</b> of the <code>resource</code> object
                </li>
                <li className="text-black light">
                  Use <b>compact</b> JSON (no added whitespace or line breaks)
                </li>
                <li className="text-black light">
                  Do <b>not</b> escape forward slashes (<code>/</code>) or non-ASCII characters
                </li>
              </ul>
              <div className="my-4 callout">
                <p className="mb-0 text-black light">
                  <strong>NOTE:</strong> Most JSON libraries meet these requirements by default.
                  Some do not. For example, PHP&apos;s <code>json_encode</code> escapes slashes and
                  unicode unless <code>JSON_UNESCAPED_SLASHES</code> and{' '}
                  <code>JSON_UNESCAPED_UNICODE</code> are set. If genuine webhooks fail
                  verification, check your serializer settings first.
                </p>
              </div>
              <h2 className="font-normal text-black">Public Key Format</h2>
              <p className="text-black light" style={longText}>
                The public key must be in <b>PEM</b> format. If the key is returned as a bare Base64
                value, wrap it in PEM headers before use:
              </p>
              <CopyBlock
                text={`-----BEGIN PUBLIC KEY-----
{Base64 key, split into 64-character lines}
-----END PUBLIC KEY-----`}
                language="text"
                theme={dracula}
                customStyle={codeStyle}
              />
              <h2 className="font-normal text-black">Security Recommendations</h2>
              <ul className="ml-5 list-disc">
                <li className="text-black light">
                  <b>Verify before acting.</b> Never update an order, invoice, or payment status
                  from an unverified webhook.
                </li>
                <li className="text-black light">
                  <b>Fail closed.</b> Treat a missing key, missing signature, or any verification
                  error as a failed verification.
                </li>
                <li className="text-black light">
                  <b>Process each event once.</b> Webhooks may be delivered more than once (see{' '}
                  <a onClick={openSection} href="#acknowledge-and-retry" style={linkStyle}>
                    Acknowledge &amp; Retry
                  </a>
                  ). Record each processed event and ignore repeats. For transactions, a combination
                  of <code>transactionId</code> and <code>status</code> is recommended, since a
                  single transaction reports multiple statuses over its lifetime (e.g.,{' '}
                  <code>PENDINGSETTLEMENT</code> → <code>SETTLED</code>).
                </li>
                <li className="text-black light">
                  <b>Use HTTPS.</b> Only register HTTPS endpoints.
                </li>
                <li className="text-black light">
                  <b>Do not log sensitive data.</b> Avoid logging full payloads in production.
                </li>
              </ul>
            </Section>

            <Section id="acknowledge-and-retry" title="Acknowledge & Retry">
              <p className="text-black light" style={longText}>
                Webhook notifications are sent within a few seconds of a transaction, settlement, or
                invoice being created or updated.
              </p>
              <h2 className="font-normal text-black">Acknowledging a Webhook</h2>
              <p className="text-black light" style={longText}>
                Webhooks must be acknowledged with a <b>200</b> response. Any other response, or no
                response, is treated as <b>undelivered</b>.
              </p>
              <h2 className="font-normal text-black">Retry Behavior</h2>
              <p className="text-black light" style={longText}>
                Undelivered webhooks are retried using an <b>exponential backoff</b> strategy until
                the retry maximum is reached.
              </p>
              <h2 className="font-normal text-black">Recommended Usage</h2>
              <ul className="ml-5 list-disc">
                <li className="text-black light">
                  Return <code>200</code> once the signature is verified and the event is recorded
                </li>
                <li className="text-black light">
                  Perform long-running work (fulfillment, emails, reporting) after acknowledging, so
                  the response is not delayed
                </li>
                <li className="text-black light">
                  Return <code>200</code> for verified events your system does not need to act on,
                  so they are not retried
                </li>
                <li className="text-black light">
                  Return a non-<code>200</code> response for webhooks that fail verification
                </li>
              </ul>
            </Section>

            <Section id="webhook-payload-examples" title="Webhook Payload Examples">
              <h2 className="font-normal text-black">Transaction Example</h2>
              <CopyBlock
                text={`{
  "resourceType": "TRANSACTION",
  "subType": "Sale",
  "resource": {
    "transactionId": "<guid>",
    "referencedTransactionId": "<guid>",
    "invoiceId": "<guid>",
    "type": "SALE",
    "authorizationSubtype": "ESTIMATED",
    "paymentMethod": "CARD",
    "paymentType": "CARD",
    "poNumber": "string",
    "ipAddress": "string",
    "description": "string",
    "transactionSource": "Api",
    "emailReceipt": true,
    "emailAddress": "string",
    "status": "PENDINGSETTLEMENT",
    "payableBatchId": "<guid>",
    "response": "string",
    "responseCode": 0,
    "authCode": "string",
    "processorResponseCode": "string",
    "processorResponseText": "string",
    "processor": {
      "processorId": "<guid>",
      "name": "string",
      "type": "string",
      "merchantId": "string",
      "groupId": "string",
      "isVirtualTerminal": true,
      "isCardPresent": true
    },
    "transactionRemit": {
      "baseAmount": 0,
      "amount": 0,
      "amountAuthorized": 0,
      "amountCaptured": 0,
      "amountSettled": 0,
      "amountRefunded": 0,
      "taxAmount": 0,
      "paymentAdjustmentType": 0,
      "paymentAdjustmentValue": 0,
      "tip": 0,
      "cashBack": 0,
      "taxExempt": true,
      "shippingAmount": 0,
      "surcharge": 0,
      "discountAmount": 0,
      "serviceFee": 0,
      "nationalTaxAmount": 0,
      "dutyAmount": 0,
      "currencyCode": 0
    },
    "transactionAddresses": [
      {
        "transactionAddressId": "<guid>",
        "isShipping": true,
        "isBilling": true,
        "firstName": "string",
        "lastName": "string",
        "company": "string",
        "email": "string",
        "phone": "string",
        "fax": "string",
        "addressLine1": "string",
        "addressLine2": "string",
        "city": "string",
        "state": "string",
        "postalCode": "string",
        "country": "string"
      }
    ],
    "transactionPaymentMethod": {
      "transactionPaymentMethodId": "<guid>",
      "ach": {
        "maskedAccount": "string",
        "accountType": "Checking",
        "secCode": "CCD",
        "checkNum": 0
      },
      "card": {
        "maskedCard": "string",
        "cardType": "VISA",
        "expirationDate": "string",
        "token": "string",
        "tenderType": "CREDIT",
        "productSubType": "SNAP",
        "emvTlvToken": "string",
        "hasAssociatedCvv": true
      },
      "avsResponse": "string",
      "cvvResponse": "string",
      "cardHolderName": "string",
      "cardEntryType": 0
    },
    "customer": {
      "customerId": "<guid>",
      "name": "string"
    },
    "createdDateTime": "2024-02-06T13:33:24.051Z",
    "modifiedDateTime": "2024-02-06T13:33:24.051Z",
    "capturedDateTime": "2024-02-06T13:33:24.051Z",
    "settledDateTime": "2024-02-06T13:33:24.051Z",
    "settlementId": "<guid>",
    "timeZone": "string",
    "systemTraceAuditNumber": "string",
    "terminal": {
      "terminalId": "<guid>",
      "sourceTerminalId": "string",
      "sourceDatawireId": "string"
    },
    "createdByUser": {
      "gatewayUserId": "<guid>",
      "firstName": "string",
      "lastName": "string"
    },
    "gateway": {
      "gatewayId": "<guid>",
      "name": "string",
      "categoryCode": "string"
    },
    "customFieldEntries": [
      {
        "customFieldId": 0,
        "name": "string",
        "value": "string",
        "modifiedByUserId": "<guid>",
        "modifiedDateTime": "2024-02-06T13:33:24.051Z",
        "type": "string",
        "customFieldCategoryName": "string",
        "customFieldCategoryId": 0,
        "dataType": "string"
      }
    ],
    "features": [
      {
        "code": "string",
        "displayName": "string"
      }
    ]
  },
  "traceId": "string",
  "actionDateTime": "2024-02-06T13:33:24.051Z",
  "hashAlg": "SHA256",
  "signatureAlg": "RSA",
  "signature": "string"
}`}
                language="json"
                theme={dracula}
                customStyle={codeStyle}
              />
              <h2 className="font-normal text-black">Settlement Example</h2>
              <CopyBlock
                text={`{
  "resourceType": "SETTLEMENT",
  "subType": "string",
  "resource": {
    "settlementId": "<guid>",
    "gatewayId": "<guid>",
    "processorId": "<guid>",
    "processorName": "<string>",
    "batchDate": "<ISO8601>",
    "transactionsCount": 0,
    "capturedAmount": 0,
    "creditAmount": 0,
    "surchargeAmount": 0,
    "paymentAdjustmentAmount": 0,
    "baseAmount": 0,
    "netAmount": 0,
    "netDepositAmount": 0,
    "responseCode": 0,
    "responseMessage": "<string>",
    "sourceLastActionAt": "<ISO8601>"
  },
  "traceId": "string",
  "actionDateTime": "2024-02-06T13:33:24.051Z",
  "hashAlg": "SHA256",
  "signatureAlg": "RSA",
  "signature": "string"
}`}
                language="json"
                theme={dracula}
                customStyle={codeStyle}
              />
              <h2 className="font-normal text-black">Invoice Example</h2>
              <CopyBlock
                text={`{
  "resourceType": "INVOICE",
  "subType": "",
  "resource": {
    "invoiceId": "<guid>",
    "title": "string",
    "number": "string",
    "invoiceStatus": {
      "invoiceStatusId": 0,
      "name": "string"
    },
    "invoiceType": {
      "invoiceTypeId": 0,
      "name": "string"
    },
    "amountCaptured": 0,
    "remainingBalance": 0,
    "subtotal": 0,
    "shipping": 0,
    "total": 0,
    "tax": 0,
    "fees": 0,
    "discounts": 0,
    "requireShippingInfo": true,
    "requireBillingInfo": true,
    "invoiceDate": "string",
    "dueDate": "string",
    "message": "string",
    "createdByUserId": "<guid>",
    "createdDateTime": "string",
    "modifiedByUserId": "<guid>",
    "modifiedDateTime": "string",
    "revisionNumber": 0,
    "gateway": {
      "gatewayId": "<guid>",
      "name": "string",
      "timezone": "string"
    },
    "customer": {
      "customerId": "<guid>",
      "name": "string"
    },
    "invoicePaymentMethod": {
      "invoicePaymentMethodId": "<guid>",
      "cardProcessorId": "<guid>",
      "achProcessorId": "<guid>",
      "secCode": 0,
      "card": true,
      "ach": true
    },
    "lineItems": [
      {
        "lineItemId": "<guid>",
        "name": "string",
        "description": "string",
        "quantity": 0,
        "unitPrice": 0,
        "discount": 0,
        "shippedAmount": 0,
        "freightAmount": 0,
        "unitOfMeasure": {
          "unitOfMeasureId": 0,
          "name": "string"
        },
        "isTaxable": true,
        "localTaxPercent": 0,
        "nationalTaxPercent": 0
      }
    ],
    "invoiceAddresses": [
      {
        "invoiceAddressId": "<guid>",
        "addressId": "<guid>",
        "isShipping": true,
        "isBilling": true,
        "isRemittance": true,
        "firstName": "string",
        "lastName": "string",
        "company": "string",
        "email": "string",
        "phone": "string",
        "fax": "string",
        "addressLine1": "string",
        "addressLine2": "string",
        "city": "string",
        "state": "string",
        "postalCode": "string",
        "country": "string"
      }
    ],
    "invoiceNotifications": [
      {
        "invoiceNotificationGroupId": "<guid>",
        "notificationMethod": {
          "invoiceNotificationMethodId": 0,
          "name": "string"
        },
        "notificationStatus": {
          "invoiceNotificationStatusId": 0,
          "name": "string"
        },
        "notificationEventType": {
          "invoiceNotificationEventTypeId": 0,
          "name": "string"
        },
        "mainRecipient": "string",
        "cc": ["string"],
        "bcc": ["string"],
        "deliveryDateTime": "string"
      }
    ],
    "transactions": [
      {
        "transactionId": "<guid>",
        "transactionType": 0,
        "paymentType": 0,
        "status": 0,
        "remit": {
          "settledAmount": 0,
          "capturedAmount": 0,
          "baseAmount": 0,
          "authorizedAmount": 0,
          "taxAmount": 0,
          "surchargeAmount": 0,
          "paymentAdjustmentAmount": 0,
          "paymentAdjustmentType": 0,
          "ach": {
            "maskedAccount": "string",
            "accountType": 0
          },
          "card": {
            "maskedCard": "string",
            "cardType": 0
          },
          "settledDateTime": "string"
        },
        "createdDateTime": "string"
      }
    ],
    "customFieldEntries": [
      {
        "customFieldId": 0,
        "name": "string",
        "value": "string",
        "modifiedByUserId": "<guid>",
        "modifiedDateTime": "<datetime>",
        "type": "string",
        "customFieldCategoryName": "string",
        "customFieldCategoryId": 0,
        "dataType": "string"
      }
    ]
  },
  "traceId": "string",
  "actionDateTime": "2024-02-06T13:33:24.051Z",
  "hashAlg": "SHA256",
  "signatureAlg": "RSA",
  "signature": "string"
}`}
                language="json"
                theme={dracula}
                customStyle={codeStyle}
              />
            </Section>

            <Section id="recommended-end-to-end-flow" title="Recommended End-to-End Flow">
              <ol className="ml-5 list-decimal">
                <li className="text-black light">
                  Create the webhook in the iQ Pro+ UI (
                  <b>Developer Hub → Webhooks → Create New +</b>)
                </li>
                <li className="text-black light">
                  Retrieve the webhook public key using{' '}
                  <code className="break-words">
                    {'GET /api/gateway/{gatewayId}/configuration'}
                  </code>
                </li>
                <li className="text-black light">Store the public key for each environment</li>
                <li className="text-black light">
                  Receive the webhook <code>POST</code> at your endpoint
                </li>
                <li className="text-black light">
                  Verify the <code>signature</code> against the serialized <code>resource</code>{' '}
                  object using the public key
                </li>
                <li className="text-black light">Reject the webhook if verification fails</li>
                <li className="text-black light">
                  Check whether the event has already been processed, and skip it if so
                </li>
                <li className="text-black light">
                  Respond with <code>200</code>
                </li>
                <li className="text-black light">
                  Process the event using <code>resourceType</code>, <code>subType</code>, and the{' '}
                  <code>resource</code> data
                </li>
              </ol>
            </Section>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default DeveloperWebhooks;
